import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount || 0);
};

const FinancialSummaryCard = ({ calculatedTotals, orderData, formData, onFormChange, editable = true }) => {
  const balanceDue = calculatedTotals.grandTotal - (orderData.deposit_paid || 0);

  return (
    <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-white">Financial Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          <div className="flex justify-between text-sm">
            <span className="text-blue-100">Subtotal</span>
            <span className="font-medium tabular-nums whitespace-nowrap">{formatCurrency(calculatedTotals.subtotal)}</span>
          </div>
          
          {/* Discount Row - Editable */}
          <div className="flex justify-between items-center text-sm gap-2">
            <div className="flex items-center gap-2 flex-1">
              <span className="text-blue-100 min-w-[60px]">Discount</span>
              {editable && (
                <div className="flex items-center gap-2">
                  <Select 
                    value={formData.discount_type || '__none__'} 
                    onValueChange={(value) => onFormChange('discount_type', value)}
                  >
                    <SelectTrigger className="h-7 w-24 text-xs bg-blue-700 border-blue-500 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      <SelectItem value="percentage">%</SelectItem>
                      <SelectItem value="fixed">Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.discount_type !== '__none__' && (
                    <Input
                      type="number"
                      value={formData.discount_value || 0}
                      onChange={(e) => onFormChange('discount_value', e.target.value)}
                      className="h-7 w-16 text-xs bg-blue-700 border-blue-500 text-white placeholder:text-blue-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                  )}
                </div>
              )}
            </div>
            <span className="font-medium tabular-nums whitespace-nowrap shrink-0">-{formatCurrency(calculatedTotals.discount)}</span>
          </div>

          {/* GST Row - Editable */}
          <div className="flex justify-between items-center text-sm gap-2">
            <div className="flex items-center gap-2 flex-1">
              <span className="text-blue-100 min-w-[60px]">GST</span>
              {editable && (
                <>
                  <Input
                    type="number"
                    value={formData.gst_percentage || 0}
                    onChange={(e) => onFormChange('gst_percentage', e.target.value)}
                    className="h-7 w-14 text-xs bg-blue-700 border-blue-500 text-white placeholder:text-blue-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0"
                  />
                  <span className="text-blue-100 text-xs">({calculatedTotals.gstPercentage}%)</span>
                </>
              )}
            </div>
            <span className="font-medium tabular-nums whitespace-nowrap shrink-0">{formatCurrency(calculatedTotals.gst)}</span>
          </div>

          <div className="flex justify-between pt-3 border-t border-blue-400/30 text-base font-semibold">
            <span>Total</span>
            <span className="tabular-nums whitespace-nowrap">{formatCurrency(calculatedTotals.grandTotal)}</span>
          </div>

          <div className="flex justify-between text-sm pt-2">
            <span className="text-blue-100">Paid</span>
            <span className="font-medium tabular-nums whitespace-nowrap">{formatCurrency(orderData.deposit_paid || 0)}</span>
          </div>

          <div className="flex justify-between pt-2 border-t border-blue-400/30 text-lg font-bold">
            <span className="text-amber-200">Balance Due</span>
            <span className="text-amber-200 tabular-nums whitespace-nowrap">{formatCurrency(balanceDue)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FinancialSummaryCard;